import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../components/Layout';
import { useSectionAccess } from '../hooks/useSectionAccess';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Textarea } from '../components/ui/textarea';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../components/ui/collapsible';
import {
  Receipt, Plus, Search, Filter, Download, Upload, Trash2,
  ArrowUpRight, ArrowDownRight, Calendar, FileText, AlertCircle,
  Check, X, RefreshCw, FileSpreadsheet, Eye, Edit, DollarSign,
  Settings, Database, ChevronDown, ChevronUp, Save, TestTube2, 
  Server, EyeOff, Loader2, CheckCircle, BarChart3
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../api';

const transactionTypes = [
  { value: 'receipt', label: 'Receipt', icon: ArrowDownRight, color: 'text-green-600' },
  { value: 'payment', label: 'Payment', icon: ArrowUpRight, color: 'text-red-600' },
  { value: 'sales', label: 'Sales', icon: Receipt, color: 'text-blue-600' },
  { value: 'purchase', label: 'Purchase', icon: FileText, color: 'text-orange-600' },
  { value: 'journal', label: 'Journal', icon: FileSpreadsheet, color: 'text-purple-600' },
  { value: 'contra', label: 'Contra', icon: RefreshCw, color: 'text-gray-600' },
];

const gstRates = [0, 5, 12, 18, 28];

const AccountingTransactions = ({ user, setUser }) => {
  const [loading, setLoading] = useState(true);
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState('');
  const [transactions, setTransactions] = useState([]);
  const [ledgers, setLedgers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [locations, setLocations] = useState([]);
  
  // Tally Prime Configuration State
  const [tallySettingsOpen, setTallySettingsOpen] = useState(false);
  const [tallyConnections, setTallyConnections] = useState([]);
  const [tallyLoading, setTallyLoading] = useState(false);
  const [tallyTesting, setTallyTesting] = useState(false);
  const [tallySaving, setTallySaving] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [tallyFormData, setTallyFormData] = useState({
    id: null,
    connection_name: '',
    tally_server_url: 'http://localhost',
    tally_port: '9000',
    tally_company_name: '',
    username: '',
    password: '',
    is_active: true
  });
  
  // Tally Sync State
  const [syncStatus, setSyncStatus] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [syncType, setSyncType] = useState('all');
  const [syncFromDate, setSyncFromDate] = useState(() => {
    const date = new Date();
    date.setMonth(date.getMonth() - 1);
    return date.toISOString().split('T')[0];
  });
  const [syncToDate, setSyncToDate] = useState(() => new Date().toISOString().split('T')[0]);
  const [syncSchedule, setSyncSchedule] = useState('manual');
  const [showSyncedDataModal, setShowSyncedDataModal] = useState(false);
  const [syncedDataTab, setSyncedDataTab] = useState('ledgers');
  const [syncedLedgers, setSyncedLedgers] = useState([]);
  const [syncedVouchers, setSyncedVouchers] = useState([]);
  const [syncedStockItems, setSyncedStockItems] = useState([]);
  
  // Filters
  const [dateFrom, setDateFrom] = useState(() => {
    const date = new Date();
    date.setMonth(date.getMonth() - 1);
    return date.toISOString().split('T')[0];
  });
  const [dateTo, setDateTo] = useState(() => new Date().toISOString().split('T')[0]);
  const [filterType, setFilterType] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  
  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    transaction_type: 'journal',
    date: new Date().toISOString().split('T')[0],
    narration: '',
    project_id: '',
    location_id: '',
    is_gst_applicable: false,
    gst_rate: 0,
    line_items: [
      { ledger_id: '', amount: 0, is_debit: true, narration: '' },
      { ledger_id: '', amount: 0, is_debit: false, narration: '' }
    ]
  });
  
  // Import state
  const [importFile, setImportFile] = useState(null);
  const [importing, setImporting] = useState(false);
  
  // Tally File Import State
  const [showTallyImportModal, setShowTallyImportModal] = useState(false);
  const [tallyImportFile, setTallyImportFile] = useState(null);
  const [tallyImportDataType, setTallyImportDataType] = useState('auto');
  const [tallyImportReplaceExisting, setTallyImportReplaceExisting] = useState(false);
  const [tallyImporting, setTallyImporting] = useState(false);
  const [applyingToAccounting, setApplyingToAccounting] = useState(false);
  const tallyFileInputRef = React.useRef(null);
  
  // Import Summary State
  const [importSummary, setImportSummary] = useState(null);
  const [loadingImportSummary, setLoadingImportSummary] = useState(false);

  useEffect(() => {
    fetchCompanies();
  }, []);

  useEffect(() => {
    if (selectedCompany) {
      fetchTransactions();
      fetchMasterData();
      fetchTallyConnections();
    }
  }, [selectedCompany, dateFrom, dateTo, filterType]);

  const fetchCompanies = async () => {
    try {
      const response = await api.get('/accounting/companies');
      setCompanies(response.data);
      if (response.data.length > 0) {
        setSelectedCompany(response.data[0].id);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch import summary for a Tally connection
  const fetchImportSummary = async (connectionId) => {
    if (!connectionId) return;
    try {
      setLoadingImportSummary(true);
      const response = await api.get(`/accounting/tally-connections/${connectionId}/import-summary`);
      setImportSummary(response.data);
    } catch (error) {
      console.error('Error fetching import summary:', error);
      setImportSummary(null);
    } finally {
      setLoadingImportSummary(false);
    }
  };

  // Tally Prime Functions
  const fetchTallyConnections = async () => {
    try {
      setTallyLoading(true);
      const params = selectedCompany ? `?company_id=${selectedCompany}` : '';
      const response = await api.get(`/accounting/tally-connections${params}`);
      setTallyConnections(response.data);
      // If there's a connection, load it into the form
      if (response.data.length > 0) {
        const conn = response.data[0];
        setTallyFormData({
          id: conn.id,
          connection_name: conn.connection_name || '',
          tally_server_url: conn.tally_server_url || 'http://localhost',
          tally_port: conn.tally_port?.toString() || '9000',
          tally_company_name: conn.tally_company_name || '',
          username: conn.username || '',
          password: '', // Don't pre-fill password
          is_active: conn.is_active !== false
        });
        // Fetch import summary for this connection
        fetchImportSummary(conn.id);
      }
    } catch (error) {
      console.error('Error fetching Tally connections:', error);
    } finally {
      setTallyLoading(false);
    }
  };

  const handleSaveTallyConnection = async () => {
    if (!tallyFormData.tally_server_url || !tallyFormData.tally_port || !tallyFormData.tally_company_name) {
      toast.error('Please fill in Host URL, Port, and Company Name');
      return;
    }

    try {
      setTallySaving(true);
      const payload = {
        company_id: selectedCompany,
        connection_name: tallyFormData.connection_name || `Tally-${selectedCompany}`,
        tally_server_url: tallyFormData.tally_server_url,
        tally_port: parseInt(tallyFormData.tally_port) || 9000,
        tally_company_name: tallyFormData.tally_company_name,
        username: tallyFormData.username,
        password: tallyFormData.password,
        is_active: tallyFormData.is_active
      };

      if (tallyFormData.id) {
        await api.put(`/accounting/tally-connections/${tallyFormData.id}`, payload);
        toast.success('Tally Prime connection updated successfully');
      } else {
        const response = await api.post('/accounting/tally-connections', payload);
        setTallyFormData(prev => ({ ...prev, id: response.data.connection?.id }));
        toast.success('Tally Prime connection saved successfully');
      }
      fetchTallyConnections();
    } catch (error) {
      console.error('Tally save error:', error);
      console.error('Error response:', error.response?.data);
      console.error('Error status:', error.response?.status);
      console.error('Request URL:', error.config?.url);
      toast.error(error.response?.data?.detail || `Failed to save Tally connection: ${error.message}`);
    } finally {
      setTallySaving(false);
    }
  };

  const handleTestTallyConnection = async () => {
    if (!tallyFormData.id) {
      toast.error('Please save the connection first before testing');
      return;
    }

    try {
      setTallyTesting(true);
      const response = await api.post(`/accounting/tally-connections/${tallyFormData.id}/test`);
      if (response.data.status === 'success' || response.data.status === 'simulated' || response.data.status === 'pending') {
        toast.success(response.data.message || 'Connection test initiated');
      } else {
        toast.error(response.data.message || 'Connection test failed');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Connection test failed');
    } finally {
      setTallyTesting(false);
    }
  };

  const handleDeleteTallyConnection = async () => {
    if (!tallyFormData.id) return;
    
    if (!window.confirm('Are you sure you want to delete this Tally connection?')) {
      return;
    }

    try {
      await api.delete(`/accounting/tally-connections/${tallyFormData.id}`);
      toast.success('Tally connection deleted');
      setTallyFormData({
        id: null,
        connection_name: '',
        tally_server_url: 'http://localhost',
        tally_port: '9000',
        tally_company_name: '',
        username: '',
        password: '',
        is_active: true
      });
      setSyncStatus(null);
      fetchTallyConnections();
    } catch (error) {
      toast.error('Failed to delete connection');
    }
  };

  // Tally Sync Functions
  const fetchSyncStatus = async () => {
    if (!tallyFormData.id) return;
    
    try {
      const response = await api.get(`/accounting/tally-connections/${tallyFormData.id}/sync-status`);
      setSyncStatus(response.data);
      setSyncSchedule(response.data.sync_schedule || 'manual');
    } catch (error) {
      console.error('Error fetching sync status:', error);
    }
  };

  const handleSyncFromTally = async () => {
    if (!tallyFormData.id) {
      toast.error('Please save the connection first');
      return;
    }

    try {
      setSyncing(true);
      
      // Convert dates to Tally format (DD-MMM-YYYY)
      const formatTallyDate = (dateStr) => {
        const date = new Date(dateStr);
        const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        return `${String(date.getDate()).padStart(2, '0')}-${months[date.getMonth()]}-${date.getFullYear()}`;
      };

      const payload = {
        sync_type: syncType,
        from_date: formatTallyDate(syncFromDate),
        to_date: formatTallyDate(syncToDate)
      };

      const response = await api.post(`/accounting/tally-connections/${tallyFormData.id}/sync`, payload);
      
      const results = response.data;
      const totalSynced = (results.ledgers?.synced || 0) + (results.vouchers?.synced || 0) + (results.stock_items?.synced || 0);
      
      if (results.status === 'completed') {
        toast.success(`Sync completed! ${totalSynced} items synced from Tally Prime.`);
      } else {
        toast.warning(`Sync completed with some issues. Check the sync status for details.`);
      }
      
      fetchSyncStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Sync failed. Please check Tally connection.');
    } finally {
      setSyncing(false);
    }
  };

  const handleUpdateSyncSchedule = async (newSchedule) => {
    if (!tallyFormData.id) return;
    
    try {
      await api.put(`/accounting/tally-connections/${tallyFormData.id}/sync-schedule`, {
        schedule: newSchedule
      });
      setSyncSchedule(newSchedule);
      toast.success(`Sync schedule updated to ${newSchedule}`);
    } catch (error) {
      toast.error('Failed to update sync schedule');
    }
  };

  const fetchSyncedData = async (dataType) => {
    if (!tallyFormData.id) return;
    
    try {
      if (dataType === 'ledgers') {
        const response = await api.get(`/accounting/tally-connections/${tallyFormData.id}/synced-ledgers?limit=100`);
        setSyncedLedgers(response.data);
      } else if (dataType === 'vouchers') {
        const response = await api.get(`/accounting/tally-connections/${tallyFormData.id}/synced-vouchers?limit=100`);
        setSyncedVouchers(response.data);
      } else if (dataType === 'stock_items') {
        const response = await api.get(`/accounting/tally-connections/${tallyFormData.id}/synced-stock-items?limit=100`);
        setSyncedStockItems(response.data);
      }
    } catch (error) {
      console.error('Error fetching synced data:', error);
    }
  };

  const handleClearSyncedData = async (dataType = null) => {
    if (!tallyFormData.id) return;
    
    const confirmMsg = dataType 
      ? `Clear all synced ${dataType}? This cannot be undone.`
      : 'Clear ALL synced data? This cannot be undone.';
    
    if (!window.confirm(confirmMsg)) return;
    
    try {
      const url = dataType 
        ? `/accounting/tally-connections/${tallyFormData.id}/synced-data?data_type=${dataType}`
        : `/accounting/tally-connections/${tallyFormData.id}/synced-data`;
      
      const response = await api.delete(url);
      toast.success('Synced data cleared');
      fetchSyncStatus();
      fetchSyncedData(syncedDataTab);
    } catch (error) {
      toast.error('Failed to clear synced data');
    }
  };

  // Handle Tally File Import
  const handleTallyFileImport = async () => {
    if (!tallyFormData.id || !tallyImportFile) {
      toast.error('Please select a file to import');
      return;
    }

    setTallyImporting(true);
    try {
      const formData = new FormData();
      formData.append('file', tallyImportFile);
      formData.append('data_type', tallyImportDataType);
      formData.append('replace_existing', tallyImportReplaceExisting);

      const response = await api.post(
        `/accounting/tally-connections/${tallyFormData.id}/import-file`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );

      if (response.data.status === 'success') {
        const imported = response.data.imported;
        const total = Object.values(imported).reduce((a, b) => a + b, 0);
        toast.success(`Successfully imported ${total} items: ${Object.entries(imported).map(([k, v]) => `${k}: ${v}`).join(', ')}`);
        setShowTallyImportModal(false);
        setTallyImportFile(null);
        fetchSyncStatus();
        fetchSyncedData(syncedDataTab);
      } else {
        toast.error(response.data.errors?.join(', ') || 'Import failed');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Import failed');
    } finally {
      setTallyImporting(false);
    }
  };

  const downloadTallyTemplate = async (dataType) => {
    if (!tallyFormData.id) {
      toast.error('Please configure Tally connection first');
      return;
    }
    try {
      const response = await api.get(
        `/accounting/tally-connections/${tallyFormData.id}/download-template/${dataType}`,
        { responseType: 'blob' }
      );
      const blob = new Blob([response.data], { 
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `tally_${dataType}_template.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Failed to download template');
    }
  };

  // Apply Tally data to main accounting module
  const handleApplyToAccounting = async () => {
    if (!tallyFormData.id) {
      toast.error('Please configure Tally connection first');
      return;
    }

    setApplyingToAccounting(true);
    try {
      const response = await api.post(
        `/accounting/tally-connections/${tallyFormData.id}/apply-to-accounting`,
        null,
        { params: { apply_ledgers: true, apply_vouchers: true, clear_existing: true } }
      );

      if (response.data.status === 'success') {
        const applied = response.data.applied;
        const summary = response.data.financial_summary;
        
        // Update import summary with the returned data
        if (summary) {
          setImportSummary(prev => ({
            ...prev,
            import_status: {
              ...prev?.import_status,
              applied_ledgers: summary.ledger_count,
              applied_vouchers: summary.voucher_count,
              is_applied: true
            },
            financial_summary: {
              total_revenue: summary.total_revenue,
              total_expenses: summary.total_expenses,
              net_profit: summary.net_profit,
              profit_margin: summary.total_revenue > 0 ? 
                ((summary.net_profit / summary.total_revenue) * 100).toFixed(1) : 0
            },
            breakdown: {
              revenue_by_ledger: summary.revenue_breakdown,
              expense_by_ledger: summary.expense_breakdown
            }
          }));
        }
        
        toast.success(`Applied to accounting: ${Object.entries(applied).map(([k, v]) => `${k}: ${v}`).join(', ')}`);
        // Refresh the page data
        fetchTransactions();
        fetchLedgers();
        // Also refresh full import summary
        fetchImportSummary(tallyFormData.id);
      } else {
        toast.error(response.data.errors?.join(', ') || 'Failed to apply data');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to apply data to accounting');
    } finally {
      setApplyingToAccounting(false);
    }
  };

  // Fetch sync status when tally form data changes
  useEffect(() => {
    if (tallyFormData.id) {
      fetchSyncStatus();
    }
  }, [tallyFormData.id]);

  const fetchMasterData = async () => {
    try {
      const [ledgersRes, projectsRes, locationsRes] = await Promise.all([
        api.get(`/accounting/ledgers?company_id=${selectedCompany}`),
        api.get(`/accounting/projects?company_id=${selectedCompany}`),
        api.get(`/accounting/locations?company_id=${selectedCompany}`)
      ]);
      setLedgers(ledgersRes.data);
      setProjects(projectsRes.data);
      setLocations(locationsRes.data);
    } catch (error) {
      console.error(error);
    }
  };

  const fetchTransactions = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      params.append('company_id', selectedCompany);
      if (dateFrom) params.append('date_from', dateFrom);
      if (dateTo) params.append('date_to', dateTo);
      if (filterType !== 'all') params.append('transaction_type', filterType);
      
      const response = await api.get(`/accounting/transactions?${params.toString()}`);
      setTransactions(response.data);
    } catch (error) {
      console.error(error);
      toast.error('Failed to fetch transactions');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTransaction = async () => {
    // Validate
    const totalDebit = formData.line_items.filter(i => i.is_debit).reduce((sum, i) => sum + (parseFloat(i.amount) || 0), 0);
    const totalCredit = formData.line_items.filter(i => !i.is_debit).reduce((sum, i) => sum + (parseFloat(i.amount) || 0), 0);
    
    if (Math.abs(totalDebit - totalCredit) > 0.01) {
      toast.error(`Debit (₹${totalDebit.toFixed(2)}) and Credit (₹${totalCredit.toFixed(2)}) totals must match`);
      return;
    }
    
    if (formData.line_items.some(i => !i.ledger_id)) {
      toast.error('Please select ledger for all line items');
      return;
    }
    
    try {
      // Calculate GST amounts if applicable
      let cgst = 0, sgst = 0, igst = 0;
      if (formData.is_gst_applicable && formData.gst_rate > 0) {
        const baseAmount = totalDebit;
        cgst = parseFloat((baseAmount * (formData.gst_rate / 2) / 100).toFixed(2));
        sgst = parseFloat((baseAmount * (formData.gst_rate / 2) / 100).toFixed(2));
      }
      
      const payload = {
        company_id: selectedCompany,
        transaction_type: formData.transaction_type,
        date: formData.date,
        narration: formData.narration,
        project_id: formData.project_id || null,
        location_id: formData.location_id || null,
        is_gst_applicable: formData.is_gst_applicable,
        cgst_amount: cgst,
        sgst_amount: sgst,
        igst_amount: igst,
        line_items: formData.line_items.map(item => ({
          ledger_id: item.ledger_id,
          ledger_name: ledgers.find(l => l.id === item.ledger_id)?.name || '',
          amount: parseFloat(item.amount) || 0,
          is_debit: item.is_debit,
          narration: item.narration || formData.narration,
          project_id: formData.project_id || null,
          location_id: formData.location_id || null
        }))
      };
      
      await api.post('/accounting/transactions', payload);
      toast.success('Transaction created successfully');
      setShowCreateModal(false);
      resetForm();
      fetchTransactions();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create transaction');
    }
  };

  const handleDeleteTransaction = async (id) => {
    if (!window.confirm('Are you sure you want to delete this transaction? This will reverse the ledger balances.')) {
      return;
    }
    
    try {
      await api.delete(`/accounting/transactions/${id}`);
      toast.success('Transaction deleted');
      fetchTransactions();
      setShowDetailModal(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete');
    }
  };

  const handleImport = async () => {
    if (!importFile) {
      toast.error('Please select a CSV file');
      return;
    }
    
    try {
      setImporting(true);
      const formData = new FormData();
      formData.append('file', importFile);
      
      const response = await api.post(`/accounting/transactions/import-csv/${selectedCompany}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      toast.success(response.data.message);
      
      if (response.data.errors && response.data.errors.length > 0) {
        console.error('Import errors:', response.data.errors);
        toast.error(`${response.data.total_errors} errors occurred. Check console for details.`);
      }
      
      setShowImportModal(false);
      setImportFile(null);
      fetchTransactions();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Import failed');
    } finally {
      setImporting(false);
    }
  };

  const downloadTemplate = async () => {
    try {
      const response = await api.get('/accounting/transactions/export-template');
      const blob = new Blob([response.data.template], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'transaction_import_template.csv';
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Failed to download template');
    }
  };

  const resetForm = () => {
    setFormData({
      transaction_type: 'journal',
      date: new Date().toISOString().split('T')[0],
      narration: '',
      project_id: '',
      location_id: '',
      is_gst_applicable: false,
      gst_rate: 0,
      line_items: [
        { ledger_id: '', amount: 0, is_debit: true, narration: '' },
        { ledger_id: '', amount: 0, is_debit: false, narration: '' }
      ]
    });
  };

  const addLineItem = () => {
    setFormData({
      ...formData,
      line_items: [...formData.line_items, { ledger_id: '', amount: 0, is_debit: true, narration: '' }]
    });
  };

  const removeLineItem = (index) => {
    if (formData.line_items.length <= 2) {
      toast.error('Minimum 2 line items required');
      return;
    }
    const newItems = formData.line_items.filter((_, i) => i !== index);
    setFormData({ ...formData, line_items: newItems });
  };

  const updateLineItem = (index, field, value) => {
    const newItems = [...formData.line_items];
    newItems[index] = { ...newItems[index], [field]: value };
    setFormData({ ...formData, line_items: newItems });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  const getTypeInfo = (type) => transactionTypes.find(t => t.value === type) || transactionTypes[4];

  // Filter transactions by search
  const filteredTransactions = transactions.filter(t => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      t.voucher_number?.toLowerCase().includes(search) ||
      t.narration?.toLowerCase().includes(search) ||
      t.line_items?.some(item => item.ledger_name?.toLowerCase().includes(search))
    );
  });

  // Calculate totals
  const totalDebitSum = filteredTransactions.reduce((sum, t) => sum + (t.total_debit || 0), 0);
  const totalCreditSum = filteredTransactions.reduce((sum, t) => sum + (t.total_credit || 0), 0);

  const hasAccess = useSectionAccess('/accounting');
  if (hasAccess === false) {
    return (
      <Layout user={user} setUser={setUser}>
        <div className="flex flex-col items-center justify-center h-64">
          <AlertCircle className="h-16 w-16 text-red-500 mb-4" />
          <h2 className="text-xl font-semibold text-gray-900">Access Denied</h2>
          <p className="text-gray-500">You don't have permission to access this page.</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user} setUser={setUser}>
      <div className="space-y-6">
        {/* Tally Prime Integration Settings */}
        <Collapsible open={tallySettingsOpen} onOpenChange={setTallySettingsOpen}>
          <Card className="border-indigo-200 bg-gradient-to-r from-indigo-50/50 to-purple-50/50">
            <CollapsibleTrigger asChild>
              <CardHeader className="cursor-pointer hover:bg-indigo-50/50 transition-colors rounded-t-lg">
                <CardTitle className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-indigo-100 rounded-lg">
                      <Database className="h-5 w-5 text-indigo-600" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">Tally Prime Integration</h3>
                      <p className="text-sm font-normal text-gray-500">Configure web login credentials for Tally Prime sync</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {tallyFormData.id && (
                      <Badge className="bg-green-100 text-green-700">
                        <Check className="h-3 w-3 mr-1" />
                        Configured
                      </Badge>
                    )}
                    {tallySettingsOpen ? <ChevronUp className="h-5 w-5 text-gray-400" /> : <ChevronDown className="h-5 w-5 text-gray-400" />}
                  </div>
                </CardTitle>
              </CardHeader>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <CardContent className="pt-0">
                {tallyLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="h-6 w-6 animate-spin text-indigo-600" />
                  </div>
                ) : (
                  <div className="space-y-6">
                    {/* Info Banner */}
                    <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                      <div className="flex items-start gap-3">
                        <Server className="text-blue-600 mt-0.5" size={20} />
                        <div>
                          <p className="font-medium text-blue-900">Tally Prime Web Login Configuration</p>
                          <p className="text-sm text-blue-700 mt-1">
                            Configure your Tally Prime server details to enable data synchronization. 
                            Ensure Tally Prime is running with ODBC/Web server enabled (default port 9000).
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Configuration Form */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      <div>
                        <Label htmlFor="tally_server_url" className="text-sm font-medium">
                          Host URL <span className="text-red-500">*</span>
                        </Label>
                        <Input
                          id="tally_server_url"
                          value={tallyFormData.tally_server_url}
                          onChange={(e) => setTallyFormData({ ...tallyFormData, tally_server_url: e.target.value })}
                          placeholder="http://localhost"
                          className="mt-1"
                          data-testid="tally-host-input"
                        />
                        <p className="text-xs text-gray-500 mt-1">e.g., http://localhost or http://192.168.1.100</p>
                      </div>

                      <div>
                        <Label htmlFor="tally_port" className="text-sm font-medium">
                          Port <span className="text-red-500">*</span>
                        </Label>
                        <Input
                          id="tally_port"
                          type="number"
                          value={tallyFormData.tally_port}
                          onChange={(e) => setTallyFormData({ ...tallyFormData, tally_port: e.target.value })}
                          placeholder="9000"
                          className="mt-1"
                          data-testid="tally-port-input"
                        />
                        <p className="text-xs text-gray-500 mt-1">Default Tally port is 9000</p>
                      </div>

                      <div>
                        <Label htmlFor="tally_company_name" className="text-sm font-medium">
                          Company Name <span className="text-red-500">*</span>
                        </Label>
                        <Input
                          id="tally_company_name"
                          value={tallyFormData.tally_company_name}
                          onChange={(e) => setTallyFormData({ ...tallyFormData, tally_company_name: e.target.value })}
                          placeholder="Enter company name from Tally"
                          className="mt-1"
                          data-testid="tally-company-input"
                        />
                        <p className="text-xs text-gray-500 mt-1">Must match exactly as in Tally Prime</p>
                      </div>

                      <div>
                        <Label htmlFor="tally_username" className="text-sm font-medium">
                          Username
                        </Label>
                        <Input
                          id="tally_username"
                          value={tallyFormData.username}
                          onChange={(e) => setTallyFormData({ ...tallyFormData, username: e.target.value })}
                          placeholder="Tally username (optional)"
                          className="mt-1"
                          data-testid="tally-username-input"
                        />
                      </div>

                      <div>
                        <Label htmlFor="tally_password" className="text-sm font-medium">
                          Password
                        </Label>
                        <div className="relative mt-1">
                          <Input
                            id="tally_password"
                            type={showPassword ? "text" : "password"}
                            value={tallyFormData.password}
                            onChange={(e) => setTallyFormData({ ...tallyFormData, password: e.target.value })}
                            placeholder={tallyFormData.id ? "Leave blank to keep existing" : "Enter password"}
                            className="pr-10"
                            data-testid="tally-password-input"
                          />
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7 p-0"
                            onClick={() => setShowPassword(!showPassword)}
                          >
                            {showPassword ? <EyeOff size={14} /> : <Eye size={14} />}
                          </Button>
                        </div>
                      </div>

                      <div>
                        <Label htmlFor="connection_name" className="text-sm font-medium">
                          Connection Name
                        </Label>
                        <Input
                          id="connection_name"
                          value={tallyFormData.connection_name}
                          onChange={(e) => setTallyFormData({ ...tallyFormData, connection_name: e.target.value })}
                          placeholder="e.g., Main Office Tally"
                          className="mt-1"
                          data-testid="tally-connection-name-input"
                        />
                      </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex items-center justify-between pt-4 border-t">
                      <div className="flex items-center gap-2">
                        {tallyFormData.id && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={handleTestTallyConnection}
                            disabled={tallyTesting}
                            className="gap-2"
                            data-testid="test-tally-btn"
                          >
                            {tallyTesting ? <Loader2 className="h-4 w-4 animate-spin" /> : <TestTube2 className="h-4 w-4" />}
                            Test Connection
                          </Button>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        {tallyFormData.id && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={handleDeleteTallyConnection}
                            className="gap-2 text-red-600 hover:text-red-700 hover:bg-red-50"
                            data-testid="delete-tally-btn"
                          >
                            <Trash2 className="h-4 w-4" />
                            Delete
                          </Button>
                        )}
                        <Button
                          onClick={handleSaveTallyConnection}
                          disabled={tallySaving}
                          className="gap-2 bg-indigo-600 hover:bg-indigo-700"
                          data-testid="save-tally-btn"
                        >
                          {tallySaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                          {tallyFormData.id ? 'Update Connection' : 'Save Connection'}
                        </Button>
                      </div>
                    </div>

                    {/* Sync Section - Only show when connection is saved */}
                    {tallyFormData.id && (
                      <div className="mt-6 pt-6 border-t border-gray-200">
                        <div className="flex items-center justify-between mb-4">
                          <div className="flex items-center gap-3">
                            <div className="p-2 bg-green-100 rounded-lg">
                              <RefreshCw className="h-5 w-5 text-green-600" />
                            </div>
                            <div>
                              <h4 className="font-semibold text-gray-900">Sync Data from Tally Prime</h4>
                              <p className="text-sm text-gray-500">Pull ledgers, vouchers, and stock items</p>
                            </div>
                          </div>
                          {syncStatus && (
                            <Badge className={
                              syncStatus.sync_status === 'success' ? 'bg-green-100 text-green-700' :
                              syncStatus.sync_status === 'failed' ? 'bg-red-100 text-red-700' :
                              'bg-gray-100 text-gray-700'
                            }>
                              {syncStatus.sync_status === 'success' ? 'Last sync successful' :
                               syncStatus.sync_status === 'failed' ? 'Last sync failed' :
                               'Never synced'}
                            </Badge>
                          )}
                        </div>

                        {/* Sync Statistics */}
                        {syncStatus?.statistics && (
                          <div className="grid grid-cols-3 gap-4 mb-4">
                            <div className="p-3 bg-blue-50 rounded-lg text-center">
                              <p className="text-2xl font-bold text-blue-700">{syncStatus.statistics.ledgers || 0}</p>
                              <p className="text-xs text-blue-600">Ledgers Synced</p>
                            </div>
                            <div className="p-3 bg-purple-50 rounded-lg text-center">
                              <p className="text-2xl font-bold text-purple-700">{syncStatus.statistics.vouchers || 0}</p>
                              <p className="text-xs text-purple-600">Vouchers Synced</p>
                            </div>
                            <div className="p-3 bg-orange-50 rounded-lg text-center">
                              <p className="text-2xl font-bold text-orange-700">{syncStatus.statistics.stock_items || 0}</p>
                              <p className="text-xs text-orange-600">Stock Items Synced</p>
                            </div>
                          </div>
                        )}

                        {/* Last Sync Info */}
                        {syncStatus?.last_sync_date && (
                          <p className="text-sm text-gray-500 mb-4">
                            Last synced: {new Date(syncStatus.last_sync_date).toLocaleString()}
                            {syncStatus.last_sync_error && (
                              <span className="text-red-500 ml-2">Error: {syncStatus.last_sync_error}</span>
                            )}
                          </p>
                        )}

                        {/* Sync Options */}
                        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
                          <div>
                            <Label className="text-sm font-medium">Sync Type</Label>
                            <Select value={syncType} onValueChange={setSyncType}>
                              <SelectTrigger className="mt-1">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="all">All Data</SelectItem>
                                <SelectItem value="ledgers">Ledgers Only</SelectItem>
                                <SelectItem value="vouchers">Vouchers Only</SelectItem>
                                <SelectItem value="stock_items">Stock Items Only</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <div>
                            <Label className="text-sm font-medium">From Date (Vouchers)</Label>
                            <Input
                              type="date"
                              value={syncFromDate}
                              onChange={(e) => setSyncFromDate(e.target.value)}
                              className="mt-1"
                            />
                          </div>
                          <div>
                            <Label className="text-sm font-medium">To Date (Vouchers)</Label>
                            <Input
                              type="date"
                              value={syncToDate}
                              onChange={(e) => setSyncToDate(e.target.value)}
                              className="mt-1"
                            />
                          </div>
                          <div>
                            <Label className="text-sm font-medium">Schedule</Label>
                            <Select value={syncSchedule} onValueChange={handleUpdateSyncSchedule}>
                              <SelectTrigger className="mt-1">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="manual">Manual Only</SelectItem>
                                <SelectItem value="hourly">Every Hour</SelectItem>
                                <SelectItem value="daily">Daily</SelectItem>
                                <SelectItem value="weekly">Weekly</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        </div>

                        {/* Sync Actions */}
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <Button
                              onClick={handleSyncFromTally}
                              disabled={syncing}
                              className="gap-2 bg-green-600 hover:bg-green-700"
                              data-testid="sync-tally-btn"
                            >
                              {syncing ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
                              {syncing ? 'Syncing...' : 'Sync Now'}
                            </Button>
                            <Button
                              onClick={() => setShowTallyImportModal(true)}
                              className="gap-2 bg-blue-600 hover:bg-blue-700"
                              data-testid="import-tally-file-btn"
                            >
                              <Upload className="h-4 w-4" />
                              Import from File
                            </Button>
                            <Button
                              variant="outline"
                              onClick={() => {
                                setShowSyncedDataModal(true);
                                fetchSyncedData('ledgers');
                              }}
                              className="gap-2"
                              data-testid="view-synced-data-btn"
                            >
                              <Eye className="h-4 w-4" />
                              View Synced Data
                            </Button>
                            <Button
                              onClick={handleApplyToAccounting}
                              disabled={applyingToAccounting}
                              className="gap-2 bg-purple-600 hover:bg-purple-700"
                              data-testid="apply-to-accounting-btn"
                            >
                              {applyingToAccounting ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle className="h-4 w-4" />}
                              Apply to Accounting
                            </Button>
                          </div>
                          <Button
                            variant="outline"
                            onClick={() => handleClearSyncedData()}
                            className="gap-2 text-red-600 hover:text-red-700 hover:bg-red-50"
                            data-testid="clear-synced-data-btn"
                          >
                            <Trash2 className="h-4 w-4" />
                            Clear All Synced Data
                          </Button>
                        </div>
                        
                        {/* Import Summary Panel */}
                        {importSummary?.import_status?.is_applied && (
                          <div className="mt-6 pt-6 border-t border-gray-200">
                            <div className="flex items-center gap-3 mb-4">
                              <div className="p-2 bg-emerald-100 rounded-lg">
                                <BarChart3 className="h-5 w-5 text-emerald-600" />
                              </div>
                              <div>
                                <h4 className="font-semibold text-gray-900">Import Summary</h4>
                                <p className="text-sm text-gray-500">Financial overview of imported Tally data</p>
                              </div>
                            </div>
                            
                            {loadingImportSummary ? (
                              <div className="flex items-center justify-center py-6">
                                <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
                              </div>
                            ) : (
                              <>
                                {/* Financial KPIs */}
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                                  <div className="p-3 bg-gradient-to-br from-green-50 to-emerald-50 rounded-lg border border-green-100">
                                    <p className="text-xs text-green-600 font-medium">Total Revenue</p>
                                    <p className="text-lg font-bold text-green-700">
                                      {new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(importSummary?.financial_summary?.total_revenue || 0)}
                                    </p>
                                  </div>
                                  <div className="p-3 bg-gradient-to-br from-red-50 to-rose-50 rounded-lg border border-red-100">
                                    <p className="text-xs text-red-600 font-medium">Total Expenses</p>
                                    <p className="text-lg font-bold text-red-700">
                                      {new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(importSummary?.financial_summary?.total_expenses || 0)}
                                    </p>
                                  </div>
                                  <div className="p-3 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg border border-blue-100">
                                    <p className="text-xs text-blue-600 font-medium">Net Profit</p>
                                    <p className="text-lg font-bold text-blue-700">
                                      {new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(importSummary?.financial_summary?.net_profit || 0)}
                                    </p>
                                  </div>
                                  <div className="p-3 bg-gradient-to-br from-purple-50 to-violet-50 rounded-lg border border-purple-100">
                                    <p className="text-xs text-purple-600 font-medium">Profit Margin</p>
                                    <p className="text-lg font-bold text-purple-700">
                                      {importSummary?.financial_summary?.profit_margin || 0}%
                                    </p>
                                  </div>
                                </div>
                                
                                {/* Data Counts */}
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                                  <div className="p-2 bg-gray-50 rounded-lg text-center">
                                    <p className="text-xl font-bold text-gray-700">{importSummary?.import_status?.synced_ledgers || 0}</p>
                                    <p className="text-xs text-gray-500">Synced Ledgers</p>
                                  </div>
                                  <div className="p-2 bg-gray-50 rounded-lg text-center">
                                    <p className="text-xl font-bold text-gray-700">{importSummary?.import_status?.synced_vouchers || 0}</p>
                                    <p className="text-xs text-gray-500">Synced Vouchers</p>
                                  </div>
                                  <div className="p-2 bg-gray-50 rounded-lg text-center">
                                    <p className="text-xl font-bold text-gray-700">{importSummary?.import_status?.applied_ledgers || 0}</p>
                                    <p className="text-xs text-gray-500">Applied Ledgers</p>
                                  </div>
                                  <div className="p-2 bg-gray-50 rounded-lg text-center">
                                    <p className="text-xl font-bold text-gray-700">{importSummary?.import_status?.applied_vouchers || 0}</p>
                                    <p className="text-xs text-gray-500">Applied Vouchers</p>
                                  </div>
                                </div>
                                
                                {/* Top Revenue/Expense Breakdown */}
                                {(importSummary?.breakdown?.revenue_by_ledger || importSummary?.breakdown?.expense_by_ledger) && (
                                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    {/* Top Revenue Sources */}
                                    {Object.keys(importSummary?.breakdown?.revenue_by_ledger || {}).length > 0 && (
                                      <div className="p-3 bg-green-50/50 rounded-lg border border-green-100">
                                        <p className="text-xs font-semibold text-green-700 mb-2">Top Revenue Sources</p>
                                        {Object.entries(importSummary.breakdown.revenue_by_ledger).slice(0, 3).map(([name, amount]) => (
                                          <div key={name} className="flex justify-between items-center py-1 text-sm">
                                            <span className="text-gray-600 truncate max-w-[150px]" title={name}>{name}</span>
                                            <span className="font-medium text-green-700">
                                              {new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(amount)}
                                            </span>
                                          </div>
                                        ))}
                                      </div>
                                    )}
                                    
                                    {/* Top Expense Categories */}
                                    {Object.keys(importSummary?.breakdown?.expense_by_ledger || {}).length > 0 && (
                                      <div className="p-3 bg-red-50/50 rounded-lg border border-red-100">
                                        <p className="text-xs font-semibold text-red-700 mb-2">Top Expense Categories</p>
                                        {Object.entries(importSummary.breakdown.expense_by_ledger).slice(0, 3).map(([name, amount]) => (
                                          <div key={name} className="flex justify-between items-center py-1 text-sm">
                                            <span className="text-gray-600 truncate max-w-[150px]" title={name}>{name}</span>
                                            <span className="font-medium text-red-700">
                                              {new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(amount)}
                                            </span>
                                          </div>
                                        ))}
                                      </div>
                                    )}
                                  </div>
                                )}
                                
                                {/* Voucher Types */}
                                {importSummary?.breakdown?.voucher_types && Object.keys(importSummary.breakdown.voucher_types).length > 0 && (
                                  <div className="mt-3 flex flex-wrap gap-2">
                                    {Object.entries(importSummary.breakdown.voucher_types).map(([type, count]) => (
                                      <Badge key={type} variant="outline" className="bg-white text-xs">
                                        {type}: {count}
                                      </Badge>
                                    ))}
                                  </div>
                                )}
                              </>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </CollapsibleContent>
          </Card>
        </Collapsible>

        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Accounting Transactions</h1>
            <p className="text-gray-500">Record and manage vouchers</p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={() => setShowImportModal(true)}>
              <Upload className="h-4 w-4 mr-2" />
              Import CSV
            </Button>
            <Button onClick={() => { resetForm(); setShowCreateModal(true); }} data-testid="create-transaction-btn">
              <Plus className="h-4 w-4 mr-2" />
              New Transaction
            </Button>
          </div>
        </div>

        {/* Filters */}
        <Card>
          <CardContent className="p-4">
            <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">Company</label>
                <Select value={selectedCompany} onValueChange={setSelectedCompany}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select Company" />
                  </SelectTrigger>
                  <SelectContent>
                    {companies.map(c => (
                      <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">From Date</label>
                <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">To Date</label>
                <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">Type</label>
                <Select value={filterType} onValueChange={setFilterType}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Types</SelectItem>
                    {transactionTypes.map(t => (
                      <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="md:col-span-2">
                <label className="text-sm font-medium text-gray-700 mb-1 block">Search</label>
                <div className="relative">
                  <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
                  <Input 
                    placeholder="Search voucher, narration, ledger..." 
                    className="pl-10"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Summary Cards */}
        <div className="grid grid-cols-3 gap-4">
          <Card>
            <CardContent className="p-4 text-center">
              <p className="text-sm text-gray-500">Transactions</p>
              <p className="text-2xl font-bold text-gray-900">{filteredTransactions.length}</p>
            </CardContent>
          </Card>
          <Card className="bg-blue-50">
            <CardContent className="p-4 text-center">
              <p className="text-sm text-blue-600">Total Debit</p>
              <p className="text-2xl font-bold text-blue-900">{formatCurrency(totalDebitSum)}</p>
            </CardContent>
          </Card>
          <Card className="bg-red-50">
            <CardContent className="p-4 text-center">
              <p className="text-sm text-red-600">Total Credit</p>
              <p className="text-2xl font-bold text-red-900">{formatCurrency(totalCreditSum)}</p>
            </CardContent>
          </Card>
        </div>

        {/* Transactions List */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Receipt className="h-5 w-5" />
              Transactions
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center h-32">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              </div>
            ) : filteredTransactions.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <Receipt className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>No transactions found</p>
                <p className="text-sm">Create a new transaction or import from CSV</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b">
                    <tr>
                      <th className="text-left p-3 text-sm font-medium text-gray-600">Date</th>
                      <th className="text-left p-3 text-sm font-medium text-gray-600">Voucher #</th>
                      <th className="text-left p-3 text-sm font-medium text-gray-600">Type</th>
                      <th className="text-left p-3 text-sm font-medium text-gray-600">Narration</th>
                      <th className="text-right p-3 text-sm font-medium text-gray-600">Debit</th>
                      <th className="text-right p-3 text-sm font-medium text-gray-600">Credit</th>
                      <th className="text-center p-3 text-sm font-medium text-gray-600">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {filteredTransactions.map(txn => {
                      const typeInfo = getTypeInfo(txn.transaction_type);
                      const TypeIcon = typeInfo.icon;
                      return (
                        <tr key={txn.id} className="hover:bg-gray-50" data-testid={`txn-row-${txn.id}`}>
                          <td className="p-3 text-sm text-gray-900">{txn.date}</td>
                          <td className="p-3">
                            <span className="font-mono text-sm text-blue-600">{txn.voucher_number}</span>
                          </td>
                          <td className="p-3">
                            <Badge className={`${typeInfo.color} bg-opacity-10`}>
                              <TypeIcon className="h-3 w-3 mr-1" />
                              {typeInfo.label}
                            </Badge>
                          </td>
                          <td className="p-3 text-sm text-gray-600 max-w-xs truncate">
                            {txn.narration || '-'}
                          </td>
                          <td className="p-3 text-sm text-right font-medium text-blue-600">
                            {formatCurrency(txn.total_debit)}
                          </td>
                          <td className="p-3 text-sm text-right font-medium text-red-600">
                            {formatCurrency(txn.total_credit)}
                          </td>
                          <td className="p-3 text-center">
                            <div className="flex items-center justify-center gap-1">
                              <Button 
                                size="sm" 
                                variant="ghost" 
                                onClick={() => { setSelectedTransaction(txn); setShowDetailModal(true); }}
                              >
                                <Eye className="h-4 w-4" />
                              </Button>
                              <Button 
                                size="sm" 
                                variant="ghost" 
                                className="text-red-600 hover:text-red-700"
                                onClick={() => handleDeleteTransaction(txn.id)}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Create Transaction Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Create Transaction</DialogTitle>
          </DialogHeader>
          <div className="space-y-6 py-4">
            {/* Transaction Type & Date */}
            <div className="grid grid-cols-4 gap-4">
              <div>
                <label className="text-sm font-medium">Transaction Type *</label>
                <Select 
                  value={formData.transaction_type} 
                  onValueChange={(v) => setFormData({...formData, transaction_type: v})}
                >
                  <SelectTrigger data-testid="select-txn-type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {transactionTypes.map(t => (
                      <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium">Date *</label>
                <Input 
                  type="date" 
                  value={formData.date} 
                  onChange={(e) => setFormData({...formData, date: e.target.value})}
                  data-testid="input-txn-date"
                />
              </div>
              <div>
                <label className="text-sm font-medium">Project</label>
                <Select 
                  value={formData.project_id || 'none'} 
                  onValueChange={(v) => setFormData({...formData, project_id: v === 'none' ? '' : v})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select Project" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No Project</SelectItem>
                    {projects.map(p => (
                      <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium">Location</label>
                <Select 
                  value={formData.location_id || 'none'} 
                  onValueChange={(v) => setFormData({...formData, location_id: v === 'none' ? '' : v})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select Location" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No Location</SelectItem>
                    {locations.map(l => (
                      <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Narration */}
            <div>
              <label className="text-sm font-medium">Narration</label>
              <Textarea 
                value={formData.narration}
                onChange={(e) => setFormData({...formData, narration: e.target.value})}
                placeholder="Description of the transaction..."
                rows={2}
                data-testid="input-narration"
              />
            </div>

            {/* GST */}
            <div className="flex items-center gap-4 p-3 bg-gray-50 rounded-lg">
              <label className="flex items-center gap-2 cursor-pointer">
                <input 
                  type="checkbox" 
                  checked={formData.is_gst_applicable}
                  onChange={(e) => setFormData({...formData, is_gst_applicable: e.target.checked})}
                  className="rounded"
                />
                <span className="text-sm font-medium">GST Applicable</span>
              </label>
              {formData.is_gst_applicable && (
                <div className="flex items-center gap-2">
                  <label className="text-sm">GST Rate:</label>
                  <Select 
                    value={String(formData.gst_rate)} 
                    onValueChange={(v) => setFormData({...formData, gst_rate: parseInt(v)})}
                  >
                    <SelectTrigger className="w-24">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {gstRates.map(rate => (
                        <SelectItem key={rate} value={String(rate)}>{rate}%</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>

            {/* Line Items */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <label className="text-sm font-medium">Line Items (Double Entry)</label>
                <Button size="sm" variant="outline" onClick={addLineItem}>
                  <Plus className="h-4 w-4 mr-1" /> Add Line
                </Button>
              </div>
              <div className="space-y-2">
                <div className="grid grid-cols-12 gap-2 text-xs font-medium text-gray-500 px-2">
                  <div className="col-span-5">Ledger</div>
                  <div className="col-span-2 text-center">Dr/Cr</div>
                  <div className="col-span-3 text-right">Amount</div>
                  <div className="col-span-2"></div>
                </div>
                {formData.line_items.map((item, index) => (
                  <div key={index} className="grid grid-cols-12 gap-2 items-center bg-gray-50 p-2 rounded">
                    <div className="col-span-5">
                      <Select 
                        value={item.ledger_id || 'select'} 
                        onValueChange={(v) => updateLineItem(index, 'ledger_id', v === 'select' ? '' : v)}
                      >
                        <SelectTrigger data-testid={`select-ledger-${index}`}>
                          <SelectValue placeholder="Select Ledger" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="select" disabled>Select Ledger</SelectItem>
                          {ledgers.map(l => (
                            <SelectItem key={l.id} value={l.id}>
                              {l.name} ({l.account_type})
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="col-span-2 flex justify-center gap-1">
                      <Button 
                        size="sm" 
                        variant={item.is_debit ? 'default' : 'outline'}
                        className={item.is_debit ? 'bg-blue-600' : ''}
                        onClick={() => updateLineItem(index, 'is_debit', true)}
                      >
                        Dr
                      </Button>
                      <Button 
                        size="sm" 
                        variant={!item.is_debit ? 'default' : 'outline'}
                        className={!item.is_debit ? 'bg-red-600' : ''}
                        onClick={() => updateLineItem(index, 'is_debit', false)}
                      >
                        Cr
                      </Button>
                    </div>
                    <div className="col-span-3">
                      <Input 
                        type="number" 
                        value={item.amount || ''} 
                        onChange={(e) => updateLineItem(index, 'amount', e.target.value)}
                        placeholder="0.00"
                        className="text-right"
                        data-testid={`input-amount-${index}`}
                      />
                    </div>
                    <div className="col-span-2 flex justify-end">
                      <Button 
                        size="sm" 
                        variant="ghost" 
                        className="text-red-600"
                        onClick={() => removeLineItem(index)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
              
              {/* Totals */}
              <div className="grid grid-cols-2 gap-4 mt-4 p-3 bg-gray-100 rounded-lg">
                <div className="text-right">
                  <span className="text-sm text-gray-600">Total Debit: </span>
                  <span className="font-bold text-blue-600">
                    {formatCurrency(formData.line_items.filter(i => i.is_debit).reduce((sum, i) => sum + (parseFloat(i.amount) || 0), 0))}
                  </span>
                </div>
                <div className="text-right">
                  <span className="text-sm text-gray-600">Total Credit: </span>
                  <span className="font-bold text-red-600">
                    {formatCurrency(formData.line_items.filter(i => !i.is_debit).reduce((sum, i) => sum + (parseFloat(i.amount) || 0), 0))}
                  </span>
                </div>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateModal(false)}>Cancel</Button>
            <Button onClick={handleCreateTransaction} data-testid="submit-transaction-btn">Create Transaction</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Import Modal */}
      <Dialog open={showImportModal} onOpenChange={setShowImportModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Import Transactions from CSV</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
              <Upload className="h-10 w-10 text-gray-400 mx-auto mb-3" />
              <input 
                type="file" 
                accept=".csv"
                onChange={(e) => setImportFile(e.target.files?.[0] || null)}
                className="hidden"
                id="csv-upload"
              />
              <label htmlFor="csv-upload" className="cursor-pointer">
                <span className="text-blue-600 hover:underline">Click to upload</span>
                <span className="text-gray-500"> or drag and drop</span>
              </label>
              {importFile && (
                <p className="mt-2 text-sm text-green-600">Selected: {importFile.name}</p>
              )}
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-500">Need the template?</span>
              <Button variant="link" onClick={downloadTemplate} className="p-0">
                <Download className="h-4 w-4 mr-1" />
                Download CSV Template
              </Button>
            </div>
            <div className="bg-amber-50 p-3 rounded-lg text-sm text-amber-700">
              <p className="font-medium mb-1">CSV Format:</p>
              <p>date, transaction_type, narration, debit_ledger, credit_ledger, amount, project_code, location_code, gst_rate</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowImportModal(false)}>Cancel</Button>
            <Button onClick={handleImport} disabled={!importFile || importing}>
              {importing ? 'Importing...' : 'Import'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detail Modal */}
      <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Transaction Details</DialogTitle>
          </DialogHeader>
          {selectedTransaction && (
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4 bg-gray-50 p-4 rounded-lg">
                <div>
                  <p className="text-sm text-gray-500">Voucher Number</p>
                  <p className="font-mono font-medium">{selectedTransaction.voucher_number}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Date</p>
                  <p className="font-medium">{selectedTransaction.date}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Type</p>
                  <Badge className="capitalize">{selectedTransaction.transaction_type}</Badge>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Source</p>
                  <Badge variant="outline">{selectedTransaction.source || 'manual'}</Badge>
                </div>
              </div>
              
              {selectedTransaction.narration && (
                <div>
                  <p className="text-sm text-gray-500">Narration</p>
                  <p className="font-medium">{selectedTransaction.narration}</p>
                </div>
              )}
              
              <div>
                <p className="text-sm text-gray-500 mb-2">Line Items</p>
                <table className="w-full text-sm">
                  <thead className="bg-gray-100">
                    <tr>
                      <th className="text-left p-2">Ledger</th>
                      <th className="text-right p-2">Debit</th>
                      <th className="text-right p-2">Credit</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedTransaction.line_items?.map((item, idx) => (
                      <tr key={idx} className="border-b">
                        <td className="p-2">{item.ledger_name || item.ledger_id}</td>
                        <td className="p-2 text-right text-blue-600">
                          {item.is_debit ? formatCurrency(item.amount) : '-'}
                        </td>
                        <td className="p-2 text-right text-red-600">
                          {!item.is_debit ? formatCurrency(item.amount) : '-'}
                        </td>
                      </tr>
                    ))}
                    <tr className="font-bold bg-gray-50">
                      <td className="p-2">Total</td>
                      <td className="p-2 text-right text-blue-600">{formatCurrency(selectedTransaction.total_debit)}</td>
                      <td className="p-2 text-right text-red-600">{formatCurrency(selectedTransaction.total_credit)}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              
              {selectedTransaction.is_gst_applicable && (
                <div className="grid grid-cols-3 gap-4 p-3 bg-purple-50 rounded-lg">
                  <div className="text-center">
                    <p className="text-xs text-purple-600">CGST</p>
                    <p className="font-medium">{formatCurrency(selectedTransaction.cgst_amount)}</p>
                  </div>
                  <div className="text-center">
                    <p className="text-xs text-purple-600">SGST</p>
                    <p className="font-medium">{formatCurrency(selectedTransaction.sgst_amount)}</p>
                  </div>
                  <div className="text-center">
                    <p className="text-xs text-purple-600">IGST</p>
                    <p className="font-medium">{formatCurrency(selectedTransaction.igst_amount)}</p>
                  </div>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="destructive" onClick={() => handleDeleteTransaction(selectedTransaction?.id)}>
              <Trash2 className="h-4 w-4 mr-2" /> Delete
            </Button>
            <Button variant="outline" onClick={() => setShowDetailModal(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Synced Data Modal */}
      <Dialog open={showSyncedDataModal} onOpenChange={setShowSyncedDataModal}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Database className="h-5 w-5 text-indigo-600" />
              Synced Data from Tally Prime
            </DialogTitle>
          </DialogHeader>
          
          <Tabs value={syncedDataTab} onValueChange={(v) => { setSyncedDataTab(v); fetchSyncedData(v); }} className="flex-1 flex flex-col overflow-hidden">
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="ledgers" className="gap-2">
                <FileText className="h-4 w-4" />
                Ledgers ({syncStatus?.statistics?.ledgers || 0})
              </TabsTrigger>
              <TabsTrigger value="vouchers" className="gap-2">
                <Receipt className="h-4 w-4" />
                Vouchers ({syncStatus?.statistics?.vouchers || 0})
              </TabsTrigger>
              <TabsTrigger value="stock_items" className="gap-2">
                <FileSpreadsheet className="h-4 w-4" />
                Stock Items ({syncStatus?.statistics?.stock_items || 0})
              </TabsTrigger>
            </TabsList>
            
            <TabsContent value="ledgers" className="flex-1 overflow-auto mt-4">
              {syncedLedgers.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <FileText className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>No ledgers synced yet</p>
                  <p className="text-sm">Click "Sync Now" to pull ledgers from Tally Prime</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 sticky top-0">
                      <tr>
                        <th className="text-left p-3">Name</th>
                        <th className="text-left p-3">Parent Group</th>
                        <th className="text-right p-3">Opening Balance</th>
                        <th className="text-right p-3">Closing Balance</th>
                        <th className="text-left p-3">Synced At</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {syncedLedgers.map((ledger, idx) => (
                        <tr key={idx} className="hover:bg-gray-50">
                          <td className="p-3 font-medium">{ledger.name}</td>
                          <td className="p-3 text-gray-600">{ledger.parent || '-'}</td>
                          <td className="p-3 text-right">{formatCurrency(ledger.opening_balance)}</td>
                          <td className="p-3 text-right">{formatCurrency(ledger.closing_balance)}</td>
                          <td className="p-3 text-gray-500 text-xs">
                            {ledger.synced_at ? new Date(ledger.synced_at).toLocaleString() : '-'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </TabsContent>
            
            <TabsContent value="vouchers" className="flex-1 overflow-auto mt-4">
              {syncedVouchers.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <Receipt className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>No vouchers synced yet</p>
                  <p className="text-sm">Click "Sync Now" to pull vouchers from Tally Prime</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 sticky top-0">
                      <tr>
                        <th className="text-left p-3">Voucher #</th>
                        <th className="text-left p-3">Type</th>
                        <th className="text-left p-3">Date</th>
                        <th className="text-left p-3">Party</th>
                        <th className="text-left p-3">Narration</th>
                        <th className="text-left p-3">Synced At</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {syncedVouchers.map((voucher, idx) => (
                        <tr key={idx} className="hover:bg-gray-50">
                          <td className="p-3 font-mono">{voucher.voucher_number || '-'}</td>
                          <td className="p-3">
                            <Badge variant="outline">{voucher.voucher_type}</Badge>
                          </td>
                          <td className="p-3">{voucher.date || '-'}</td>
                          <td className="p-3">{voucher.party_name || '-'}</td>
                          <td className="p-3 text-gray-600 max-w-xs truncate">{voucher.narration || '-'}</td>
                          <td className="p-3 text-gray-500 text-xs">
                            {voucher.synced_at ? new Date(voucher.synced_at).toLocaleString() : '-'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </TabsContent>
            
            <TabsContent value="stock_items" className="flex-1 overflow-auto mt-4">
              {syncedStockItems.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <FileSpreadsheet className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>No stock items synced yet</p>
                  <p className="text-sm">Click "Sync Now" to pull stock items from Tally Prime</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 sticky top-0">
                      <tr>
                        <th className="text-left p-3">Name</th>
                        <th className="text-left p-3">Group</th>
                        <th className="text-left p-3">Unit</th>
                        <th className="text-left p-3">HSN Code</th>
                        <th className="text-right p-3">Opening Qty</th>
                        <th className="text-right p-3">Closing Qty</th>
                        <th className="text-left p-3">Synced At</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {syncedStockItems.map((item, idx) => (
                        <tr key={idx} className="hover:bg-gray-50">
                          <td className="p-3 font-medium">{item.name}</td>
                          <td className="p-3 text-gray-600">{item.parent || '-'}</td>
                          <td className="p-3">{item.base_unit || '-'}</td>
                          <td className="p-3 font-mono">{item.hsn_code || '-'}</td>
                          <td className="p-3 text-right">{item.opening_balance || 0}</td>
                          <td className="p-3 text-right">{item.closing_balance || 0}</td>
                          <td className="p-3 text-gray-500 text-xs">
                            {item.synced_at ? new Date(item.synced_at).toLocaleString() : '-'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </TabsContent>
          </Tabs>
          
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setShowSyncedDataModal(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Tally File Import Modal */}
      <Dialog open={showTallyImportModal} onOpenChange={setShowTallyImportModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Upload className="h-5 w-5 text-blue-600" />
              Import Tally Data from File
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-sm text-blue-800">
                <strong>Tip:</strong> Export data from Tally Prime using Reports → Export (Ctrl+E) → XML or Excel format
              </p>
            </div>
            
            {/* File Upload */}
            <div className="space-y-2">
              <Label>Select File (XML or Excel)</Label>
              <div 
                className="border-2 border-dashed rounded-lg p-6 text-center cursor-pointer hover:border-blue-400 transition-colors"
                onClick={() => tallyFileInputRef.current?.click()}
              >
                <input
                  type="file"
                  ref={tallyFileInputRef}
                  className="hidden"
                  accept=".xml,.xlsx,.xls"
                  onChange={(e) => setTallyImportFile(e.target.files[0])}
                />
                {tallyImportFile ? (
                  <div className="flex items-center justify-center gap-2">
                    {tallyImportFile.name.endsWith('.xml') ? (
                      <FileText className="h-8 w-8 text-orange-500" />
                    ) : (
                      <FileSpreadsheet className="h-8 w-8 text-green-500" />
                    )}
                    <div className="text-left">
                      <p className="font-medium">{tallyImportFile.name}</p>
                      <p className="text-sm text-gray-500">{(tallyImportFile.size / 1024).toFixed(1)} KB</p>
                    </div>
                  </div>
                ) : (
                  <>
                    <Upload className="h-10 w-10 mx-auto text-gray-400 mb-2" />
                    <p className="text-gray-600">Click to select file</p>
                    <p className="text-sm text-gray-400">XML or Excel (.xlsx)</p>
                  </>
                )}
              </div>
            </div>
            
            {/* Data Type Selection */}
            <div className="space-y-2">
              <Label>Data Type</Label>
              <Select value={tallyImportDataType} onValueChange={setTallyImportDataType}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="auto">Auto-detect from file</SelectItem>
                  <SelectItem value="ledgers">Ledgers (Chart of Accounts)</SelectItem>
                  <SelectItem value="vouchers">Vouchers (Transactions)</SelectItem>
                  <SelectItem value="stock_items">Stock Items (Inventory)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {/* Replace Existing Option */}
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="replace-existing-trans"
                checked={tallyImportReplaceExisting}
                onChange={(e) => setTallyImportReplaceExisting(e.target.checked)}
                className="rounded border-gray-300"
              />
              <Label htmlFor="replace-existing-trans" className="text-sm cursor-pointer">
                Replace existing data (clears previous imports)
              </Label>
            </div>
            
            {/* Download Templates */}
            <div className="border-t pt-4">
              <p className="text-sm font-medium mb-2">Download Excel Templates:</p>
              <div className="flex flex-wrap gap-2">
                <Button size="sm" variant="outline" onClick={() => downloadTallyTemplate('ledgers')}>
                  <Download className="h-3 w-3 mr-1" />
                  Ledgers
                </Button>
                <Button size="sm" variant="outline" onClick={() => downloadTallyTemplate('vouchers')}>
                  <Download className="h-3 w-3 mr-1" />
                  Vouchers
                </Button>
                <Button size="sm" variant="outline" onClick={() => downloadTallyTemplate('stock_items')}>
                  <Download className="h-3 w-3 mr-1" />
                  Stock Items
                </Button>
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTallyImportModal(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleTallyFileImport} 
              disabled={!tallyImportFile || tallyImporting}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {tallyImporting ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Upload className="h-4 w-4 mr-2" />
              )}
              Import Data
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

export default AccountingTransactions;
